import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

// Officials-only drawer for recording the finishing order of an omskyting
// (KM/DM medal tie). The shoot-off happens on the range; only the resulting
// order is stored (convention.shootOffs) — no scores are changed. Tap the
// shooters in finishing order, then save.
@observer
class ShootOffEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            order: [], // participant ids in tapped (finishing) order
            saving: false,
            error: '',
        };
    }

    toggleShooter = (id) => () => {
        const { order } = this.state;
        if (order.includes(id)) {
            this.setState({ order: order.filter(existing => existing !== id), error: '' });
        } else {
            this.setState({ order: [...order, id], error: '' });
        }
    }

    saveOrder = async () => {
        const { stores, conventionId, groupKey, resolutionMd5, participants = [] } = this.props;
        const { conventionStore, appState } = stores;
        const { convention } = conventionStore;
        const { order } = this.state;
        if (order.length !== participants.length) {
            this.setState({ error: 'Velg rekkefølgen for alle skytterne' });
            return;
        }
        this.setState({ saving: true, error: '' });
        try {
            if (resolutionMd5) {
                await conventionStore.removeArrayObject({
                    field: 'shootOffs',
                    id: conventionId,
                    md5: resolutionMd5,
                    objectName: 'convention',
                });
            }
            await conventionStore.addArrayObject({
                newObject: { key: groupKey, order },
                object: convention,
                field: 'shootOffs',
                objectName: 'convention',
            });
            this.setState({ saving: false });
            appState.toggleDrawer(false, this.props.drawerLevel);
        } catch (err) {
            this.setState({ saving: false, error: err?.message || 'Klarte ikke å lagre omskytingen' });
        }
    }

    clearResolution = async () => {
        const dialogAction = beginDialogAction(this, 'clearResolution');
        if (!dialogAction) return false;
        try {
            const { stores, conventionId, resolutionMd5 } = this.props;
            const { conventionStore, appState } = stores;
            if (!resolutionMd5) return;
            if (!((await appConfirm('Nullstille omskytingsresultatet?')) && dialogAction.isCurrent())) return;
            this.setState({ saving: true, error: '' });
            await conventionStore.removeArrayObject({
                field: 'shootOffs',
                id: conventionId,
                md5: resolutionMd5,
                objectName: 'convention',
            });
            this.setState({ saving: false });
            appState.toggleDrawer(false, this.props.drawerLevel);
        } finally {
            dialogAction.finish();
        }
    }

    render() {
        const { participants = [], totalScore, startPlace, resolutionMd5 } = this.props;
        const { order, saving, error } = this.state;
        return (
            <div class='d-flex flex-column px-3 py-3'>
                <h5 class='mb-1'>
                    <i class='fa-solid fa-repeat me-2' />
                    <Text id='shootOff.title'>Omskyting</Text>
                </h5>
                <small class='text-body-secondary mb-3'>
                    <Text id='shootOff.tied-info' fields={{ score: totalScore, place: startPlace }}>
                        Tied at {totalScore} points from place {startPlace}. Tap the shooters in finishing order from the shoot-off.
                    </Text>
                </small>
                <div class='list-group mb-3'>
                    {participants.map(participant => {
                        const position = order.indexOf(participant.id);
                        return (
                            <button
                                key={participant.id}
                                type='button'
                                class={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${position >= 0 ? 'active' : ''}`}
                                onClick={this.toggleShooter(participant.id)}
                            >
                                <span>
                                    <strong>{participant.name}</strong>
                                    {participant.sportsClub && (
                                        <small class={`ms-2 ${position >= 0 ? '' : 'text-body-secondary'}`}>{participant.sportsClub}</small>
                                    )}
                                </span>
                                {position >= 0 && (
                                    <span class='badge text-bg-light'>{position + 1}.</span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {error && (
                    <div class='alert alert-warning py-2'>{error}</div>
                )}
                <div class='d-flex justify-content-between'>
                    {resolutionMd5 ? (
                        <button type='button' class='btn btn-outline-danger' disabled={saving} onClick={this.clearResolution}>
                            <Text id='shootOff.clear'>Reset</Text>
                        </button>
                    ) : <span />}
                    <button type='button' class='btn btn-primary' disabled={saving || order.length !== participants.length} onClick={this.saveOrder}>
                        {saving && <i class='fa-duotone fa-spinner fa-spin me-1' />}
                        <Text id='shootOff.save'>Save finishing order</Text>
                    </button>
                </div>
            </div>
        );
    }
}

export default ShootOffEditor;
